import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  criticalIncidentsQuery,
  downloadReportXlsx,
  easeedeskReportsShellQuery,
  exportParams,
  groupUsersQuery,
  incidentsPriorityQuery,
  incidentsStatusQuery,
  openIncidentsQuery,
  resolvedTicketsQuery,
  serviceCostsQuery,
  type CriticalIncidents,
  type GenerateTime,
  type IncidentsPriority,
  type IncidentsStatus,
  type OpenIncidents,
  type ResolvedFilters,
  type ResolvedTickets,
  type ServiceCosts,
  type StatusKey,
} from '../api/easeedeskReports'
import { buildCostSnapshot, downloadCostsXlsx, snapshotFormQuery } from '../api/serviceCatalog'
import { CompanyReportsFrame } from './company_reports/CompanyReportsPage'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { LoadFailed } from '../ui/LoadFailed'
import { Modal } from '../ui/Modal'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'

/**
 * Raportul Easeedesk din rapoartele de firma - `settings/company_reports/
 * easeedesk_report`, macheta `views/version-3/settings/company_reports/
 * easeedesk/index.php` si cele sase fragmente de langa ea.
 *
 * SASE FELURI DIN SAPTE, cu numerele din `<select id="report_type">`:
 * 1 Critical Incidents per Category, 2 Incidents Status, 3 Incidents Assigned
 * per Support Group, 4 Incidents Trends per Priority, 5 Resolved Tickets,
 * 6 Service costs. Al saptelea, "Scheduled delivery", nu era un raport, ci
 * ecranul de programari incarcat inauntru - in v4 e fila lui,
 * `scheduled_report`, deci nu se mai desface de aici.
 *
 * RAPORTUL 5 se cere la "Apply", nu la fiecare tasta: are doua date scrise de
 * mana, si o interogare cu cincisprezece imbinari pornita la fiecare cifra ar
 * fi fost o pedeapsa pentru cine tasteaza. Celelalte raman vii, ca inainte.
 *
 * EXPORTUL (butonul verde din dreapta, ca in macheta) cere aceleasi filtre ca
 * raportul de pe ecran, si primeste fisierul pe loc. Pentru costuri (6) merge
 * pe drumul catalogului (`service_catalog/exportCosts`, aceeasi foaie ca de
 * acolo), cu luna de pe ecran la ambele capete.
 *
 * "WORK OUT A MONTH" (`#costBuild`, numai pe raportul de costuri si numai cu
 * `inventoree.service_catalog.edit`) deschide formularul catalogului
 * (`snapshot_form.php`: doua perechi de luna/an) pe luna de pe ecran; dupa
 * calcul, fila se reincarca intreaga - lunile abia calculate trebuie sa apara
 * si in lista Month.
 *
 * GRAFICELE vin DESENATE de la server, ca SVG (`IncidentPanels`, acelasi
 * fisier pe care il cheama si portalul vechi). amCharts a iesit din portal pe
 * 6 septembrie 2026 si nu se aduce inapoi. De-aia graficul se pune cu
 * `dangerouslySetInnerHTML`: e HTML facut de noi, in PHP, nu text scris de om.
 * Tot ce vine din baza - nume de categorie, de prioritate, de grup - se scrie
 * ca TEXT, prin React, deci se scapa singur.
 */

/**
 * Filtrele CU CARE s-a apasat Apply - raportul de pe ecran.
 *
 * In macheta veche, TOATE cele sase rapoarte se cereau la trimiterea
 * formularului (`#ticketsReportFilters`, `switch` pe `report_type`), nu la
 * fiecare atingere de filtru. Butonul Apply e in ecranul cerut, deci trebuie
 * sa si faca ce scrie pe el: pana la el nu pleaca nicio cerere, iar ce se
 * schimba in filtru dupa aceea nu misca cifrele de pe ecran pana la
 * urmatorul Apply.
 */
type Applied = {
  report: number
  generateTime: GenerateTime
  priority: number
  ticketTypes: number[]
  groups: number[]
  month: number
  resolved: ResolvedFilters
}

const REPORT_TYPES = {
  CRITICAL: 1,
  STATUS: 2,
  OPEN: 3,
  PRIORITY: 4,
  RESOLVED: 5,
  COSTS: 6,
} as const

/** `date('Y-m-d H:i')` - valoarea cu care macheta umplea casutele de data. */
function nowStamp(): string {
  const d = new Date()
  const two = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`
}

/** Capul de tabel al machetelor `resolved_tickets.php` / `service_costs.php`. */
const HEAD_ROW = { backgroundColor: '#32777e' } as const
const HEAD_CELL = { color: '#fff' } as const
const REPORT_TABLE = { border: '2px solid #32777e' } as const

function money(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Graficul desenat de server. Vezi capul fisierului pentru de ce e sigur. */
function ServerChart({ svg }: { svg: string }) {
  if (!svg) return null
  return <div className="od-report-chart" dangerouslySetInnerHTML={{ __html: svg }} />
}

function PeriodTable({ head, periods, rows, total }: {
  head: string
  periods: string[]
  rows: { name: string; records: number[] }[]
  total?: number[]
}) {
  return (
    <div className="table-responsive">
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>{head}</th>
            {periods.map((p) => (
              <th key={p}>{p}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td>{row.name}</td>
              {row.records.map((n, i) => (
                <td key={periods[i] ?? i}>{n}</td>
              ))}
            </tr>
          ))}
          {total ? (
            <tr>
              <td>
                <strong>TOTAL</strong>
              </td>
              {total.map((n, i) => (
                <td key={periods[i] ?? i}>
                  <strong>{n}</strong>
                </td>
              ))}
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}

function CriticalPanel({ data }: { data: CriticalIncidents }) {
  return (
    <>
      <PeriodTable
        head="Category"
        periods={data.periods}
        rows={data.rows.map((r) => ({ name: r.category_name, records: r.records }))}
        total={data.total_records}
      />
      {data.total_records.some((n) => n > 0) ? (
        <ServerChart svg={data.chart} />
      ) : (
        <div className="alert alert-info">No incidents found.</div>
      )}
    </>
  )
}

/** Ordinea si numele seriilor sunt cele din macheta `status_incidents.php`. */
const STATUS_ORDER: StatusKey[] = ['resolved', 'created', 'still_open', 'resolved_sla']

function StatusPanel({ data }: { data: IncidentsStatus }) {
  return (
    <>
      <PeriodTable
        head="Status"
        periods={data.periods}
        rows={STATUS_ORDER.map((key) => ({ name: data.names[key], records: data.series[key] ?? [] }))}
      />
      <ServerChart svg={data.chart} />
    </>
  )
}

function PriorityPanel({ data }: { data: IncidentsPriority }) {
  return (
    <>
      <PeriodTable
        head="Priority"
        periods={data.periods}
        rows={data.rows.map((r) => ({ name: r.priority_name, records: r.records }))}
      />
      <ServerChart svg={data.chart} />
    </>
  )
}

/** `open_incidents.php`: un panou pe tip de tichet, fara grafic. */
function OpenPanel({ data }: { data: OpenIncidents }) {
  if (data.types.length === 0) return <div className="alert alert-info">No open incidents found.</div>

  return (
    <div className="row">
      {data.types.map((type) => (
        <div className="col-md-6" key={type.type_id}>
          <h5>{type.type_name}</h5>
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Support Group</th>
                  <th>&lt; SLA</th>
                  <th>&gt; SLA * 2</th>
                  <th>&gt; SLA * 3</th>
                  <th>&gt; SLA * 4</th>
                </tr>
              </thead>
              <tbody>
                {type.rows.map((row) => (
                  <tr key={row.group_name}>
                    <td>{row.group_name}</td>
                    <td>{row.sla_1}</td>
                    <td>{row.sla_2}</td>
                    <td>{row.sla_3}</td>
                    <td>{row.sla_4}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <strong>TOTAL</strong>
                  </td>
                  <td>
                    <strong>{type.total.sla_1}</strong>
                  </td>
                  <td>
                    <strong>{type.total.sla_2}</strong>
                  </td>
                  <td>
                    <strong>{type.total.sla_3}</strong>
                  </td>
                  <td>
                    <strong>{type.total.sla_4}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * `resolved_tickets.php`: toate coloanele pe care le-a ales serverul, in
 * ordinea lui; descrierea scurtata la 100 de litere (fisierul o poarta
 * intreaga); cronologia si clasificarea cu liniile lor.
 */
function ResolvedPanel({ data }: { data: ResolvedTickets }) {
  const fields = Object.keys(data.columns)
  const multiline = (field: string) => field === 'status_history' || field === 'work_classification'

  return (
    <>
      <h4 style={{ margin: '20px 0' }}>Resolved Tickets</h4>
      <div className="table-responsive">
        <table className="table table-hover table-bordered" style={REPORT_TABLE}>
          <thead>
            <tr style={HEAD_ROW}>
              {fields.map((field) => (
                <th key={field} className="align-left" style={HEAD_CELL}>
                  {data.columns[field]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td colSpan={fields.length} style={{ textAlign: 'center' }}>
                  No tickets found.
                </td>
              </tr>
            ) : (
              data.rows.map((row) => (
                <tr key={row.ID ?? ''} style={{ backgroundColor: '#fff' }}>
                  {fields.map((field) => {
                    let value = row[field] ?? ''
                    if (field === 'description' && value.length > 100) value = value.slice(0, 100) + '...'
                    return (
                      <td
                        key={field}
                        className="align-left"
                        style={multiline(field) ? { whiteSpace: 'pre-line', minWidth: 220 } : undefined}
                      >
                        {value === '' ? '-' : value}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * `service_costs.php`: luna langa titlu, clientii deasupra cand sunt mai
 * multi, liniile serviciilor, si totalul PE MONEDA in subsol.
 */
function CostsPanel({ data }: { data: ServiceCosts }) {
  return (
    <>
      <h4 style={{ margin: '20px 0' }}>
        What the services cost <small style={{ color: '#777' }}>{data.period_label}</small>
      </h4>

      {data.rows.length === 0 ? (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle" aria-hidden="true"></i> This month has not been costed. Give a service a
          cost in the service catalogue, then work out a month there.
        </div>
      ) : (
        <>
          {data.totals.company.length > 1 ? (
            <>
              <h5 style={{ margin: '20px 0 8px' }}>
                Per customer <small style={{ color: '#777' }}>showback &mdash; what each one consumed</small>
              </h5>
              <table className="table table-hover table-bordered" style={REPORT_TABLE}>
                <thead>
                  <tr style={HEAD_ROW}>
                    <th className="align-left" style={HEAD_CELL}>Customer</th>
                    <th className="align-right" style={HEAD_CELL}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.totals.company.map((row) => (
                    <tr key={row.name} style={{ backgroundColor: '#fff' }}>
                      <td>
                        <strong>{row.name}</strong>
                      </td>
                      <td className="align-right">{money(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          <table className="table table-hover table-bordered" style={REPORT_TABLE}>
            <thead>
              <tr style={HEAD_ROW}>
                <th className="align-left" style={HEAD_CELL}>Service</th>
                <th className="align-left" style={HEAD_CELL}>Customer</th>
                <th className="align-left" style={HEAD_CELL}>Category</th>
                <th className="align-right" style={HEAD_CELL}>Items</th>
                <th className="align-right" style={HEAD_CELL}>Users</th>
                <th className="align-right" style={HEAD_CELL}>Tickets</th>
                <th className="align-right" style={HEAD_CELL}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={`${row.catalog_id}-${row.company_name}`} style={{ backgroundColor: '#fff' }}>
                  <td>
                    <a href={`/app/service_catalog/composition/${row.catalog_id}`} target="_blank" rel="noopener noreferrer">
                      {row.service_name}
                    </a>{' '}
                    <span className={row.kind === 4 ? 'label label-primary' : 'label label-default'}>
                      {row.kind === 4 ? 'Business' : 'IT'}
                    </span>
                  </td>
                  <td>{row.company_name}</td>
                  <td style={{ color: '#777' }}>{row.category_name}</td>
                  <td className="align-right">{row.ci_count}</td>
                  <td className="align-right">{row.user_count}</td>
                  <td className="align-right">{row.ticket_count}</td>
                  <td className="align-right">
                    <strong>{money(row.amount)}</strong> <span style={{ color: '#777' }}>{row.currency}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {data.totals.currency.map((total) => (
                <tr key={total.name} style={{ backgroundColor: '#f5f5f5' }}>
                  <td colSpan={6}>
                    <strong>Total</strong>
                  </td>
                  <td className="align-right">
                    <strong>{money(total.amount)}</strong> <span style={{ color: '#777' }}>{total.name}</span>
                  </td>
                </tr>
              ))}
            </tfoot>
          </table>
        </>
      )}
    </>
  )
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** `inventoree/service_catalog/snapshot_form.php` - doua perechi de luna/an. */
function CostSnapshotModal({ month, onClose, onDone }: { month: number; onClose(): void; onDone(period: number): void }) {
  const form = useQuery(snapshotFormQuery(month, month))
  const [from, setFrom] = useState<{ m: number; y: number } | null>(null)
  const [to, setTo] = useState<{ m: number; y: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const data = form.data
  const f = from ?? (data ? { m: data.fromYm % 100, y: Math.floor(data.fromYm / 100) } : null)
  const t = to ?? (data ? { m: data.toYm % 100, y: Math.floor(data.toYm / 100) } : null)
  const years: number[] = []
  if (data) for (let y = data.lastYear; y >= data.firstYear; y--) years.push(y)

  const submit = async () => {
    if (!f || !t) return
    setBusy(true)
    try {
      const out = await buildCostSnapshot({ from_month: f.m, from_year: f.y, to_month: t.m, to_year: t.y })
      if (out.status !== 'OK') {
        toast(out.message || 'Something went wrong', 'Error', 'error')
        return
      }
      toast('The months were worked out', 'Success')
      onDone(out.period ?? 0)
    } catch (error) {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const pair = (id: string, name: string, value: { m: number; y: number }, set: (v: { m: number; y: number }) => void) => (
    <div className="row">
      <div className="col-xs-7">
        <select className="form-control input-sm" name={`${name}_month`} id={`${id}Month`} value={value.m} onChange={(e) => set({ ...value, m: Number(e.target.value) })}>
          {MONTHS.map((label, i) => (
            <option key={i + 1} value={i + 1}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="col-xs-5">
        <select className="form-control input-sm" name={`${name}_year`} id={`${id}Year`} value={value.y} onChange={(e) => set({ ...value, y: Number(e.target.value) })}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  )

  return (
    <Modal
      id="costSnapshotModal"
      open
      onClose={onClose}
      title={
        <>
          <i className="fas fa-calculator"></i> Work out the costs
        </>
      }
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="snapshotForm" className="btn btn-primary" disabled={busy || !data}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Working them out
              </>
            ) : (
              <>
                <i className="fas fa-calculator"></i> Work them out
              </>
            )}
          </button>
        </>
      }
    >
      {form.error ? (
        <div className="alert alert-danger">That could not be loaded.</div>
      ) : !data || !f || !t ? (
        <Spinner />
      ) : (
        <form
          id="snapshotForm"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <p className="text-muted">
            Each month is worked out from the prices and the counts as they are <strong>now</strong>, and then stored. Asked
            again later, the same month gives the same answer &mdash; which is the whole point of a chargeback figure.
            <br />
            <br />
            Running it again over a month already costed replaces that month, so this is also how a corrected price is
            applied.
          </p>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>From</label>
              {pair('snapFrom', 'from', f, setFrom)}
            </div>
            <div className="col-md-6 form-group">
              <label>To</label>
              {pair('snapTo', 'to', t, setTo)}
            </div>
          </div>
          <p className="text-muted small">
            <i className="fas fa-info-circle"></i> Opens on whatever the report is showing. With nothing chosen it is last
            month, because this one is not over and a part month reads as a whole one. At most {data.maxMonths} months at a
            time.
          </p>
        </form>
      )}
    </Modal>
  )
}

export function EaseedeskReportsPage() {
  const shell = useQuery(easeedeskReportsShellQuery)
  /* Nimic ales la deschidere: butonul scrie "Select..", ca in macheta. */
  const [report, setReport] = useState<number>(0)
  /* Panoul de filtre e deschis la intrare si se strange la Apply, ca acolo
     (`$('#collapseFilter').collapse()`, apoi `removeClass('in')`). */
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [generateTime, setGenerateTime] = useState<GenerateTime>('days')
  const [priority, setPriority] = useState(0)
  const [ticketTypes, setTicketTypes] = useState<number[]>([])
  const [groups, setGroups] = useState<number[]>([])
  const [users, setUsers] = useState<number[]>([])
  const [dateFrom, setDateFrom] = useState(nowStamp)
  const [dateTo, setDateTo] = useState(nowStamp)
  const [oncall, setOncall] = useState(false)
  const [nagios, setNagios] = useState(false)
  /* Filtrele CU CARE s-a apasat Apply; pana atunci, nimic pe ecran. */
  const [applied, setApplied] = useState<Applied | null>(null)
  const [costMonth, setCostMonth] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [building, setBuilding] = useState(false)
  const queryClient = useQueryClient()

  const draft: ResolvedFilters = { groups, users, ticketTypes, dateFrom, dateTo, oncall, nagios }
  const firstMonth = shell.data?.costPeriods[0]?.ym ?? 0
  const month = costMonth > 0 ? costMonth : firstMonth

  /* Lista de oameni se umple din grupurile alese INAINTE de Apply: in macheta,
     casuta User se desena la alegerea grupului, nu la rularea raportului. */
  const groupUsers = useQuery({ ...groupUsersQuery(groups), enabled: report === REPORT_TYPES.RESOLVED && groups.length > 0 })

  /* De aici in jos totul citeste `applied`: raportul de pe ecran e cel cu care
     s-a apasat Apply, nu ce s-a mai atins in filtru de atunci. */
  const shown = applied?.report ?? 0
  const resolved = useQuery({
    ...resolvedTicketsQuery(applied?.resolved ?? draft),
    enabled: shown === REPORT_TYPES.RESOLVED,
  })
  const costs = useQuery({
    ...serviceCostsQuery(applied?.month ?? 0),
    enabled: shown === REPORT_TYPES.COSTS && (applied?.month ?? 0) > 0,
  })
  const critical = useQuery({
    ...criticalIncidentsQuery(applied?.generateTime ?? 'days', applied?.priority ?? 0, applied?.ticketTypes ?? []),
    enabled: shown === REPORT_TYPES.CRITICAL && (applied?.priority ?? 0) > 0,
  })
  const status = useQuery({
    ...incidentsStatusQuery(applied?.generateTime ?? 'days', applied?.ticketTypes ?? []),
    enabled: shown === REPORT_TYPES.STATUS,
  })
  const trends = useQuery({
    ...incidentsPriorityQuery(applied?.generateTime ?? 'days', applied?.ticketTypes ?? []),
    enabled: shown === REPORT_TYPES.PRIORITY,
  })
  const open = useQuery({
    ...openIncidentsQuery(applied?.ticketTypes ?? [], applied?.groups ?? []),
    enabled: shown === REPORT_TYPES.OPEN && (applied?.ticketTypes ?? []).length > 0,
  })

  /*
   * BARA DE FILE, ca la toate celelalte file ale rapoartelor de firma.
   *
   * Fila asta era SINGURA din sapte care nu o desena - si tocmai ea e cea pe
   * care o deschide `settings/company_reports` cand nu se cere alta, fiindca e
   * prima din bara. Omul ajungea pe rapoarte si nu vedea nicio fila: nici
   * Easeedesk, nici BI, nici Cost - desi toate unsprezece erau scrise si aveau
   * ecran. Ecranul vechi le arata pe toate, intr-o banda orizontala.
   *
   * Sta si peste iesirile devreme (403, cerere picata, spinner): o fila care
   * nu s-a incarcat nu e un motiv sa dispara drumul catre celelalte.
   */
  const framed = (body: ReactNode) => <CompanyReportsFrame current="easeedesk">{body}</CompanyReportsFrame>

  if (shell.error instanceof ApiError && shell.error.status === 403) return framed(<Forbidden />)
  /* Fara asta, un 500 pe capul filei lasa `data` gol si spinnerul se invartea la nesfarsit. */
  if (shell.error) return framed(<LoadFailed what="The Easeedesk reports" error={shell.error} onRetry={() => void shell.refetch()} />)
  if (!shell.data) return framed(<Spinner />)

  /* Raportul de pe ecran si eroarea lui - cel APLICAT, nu ce e in filtru acum. */
  const active = {
    [REPORT_TYPES.CRITICAL]: critical,
    [REPORT_TYPES.STATUS]: status,
    [REPORT_TYPES.OPEN]: open,
    [REPORT_TYPES.PRIORITY]: trends,
    [REPORT_TYPES.RESOLVED]: resolved,
    [REPORT_TYPES.COSTS]: costs,
  }[shown]
  const reportError = active?.error ?? null

  const busy = critical.isFetching || status.isFetching || trends.isFetching || open.isFetching
    || resolved.isFetching || costs.isFetching

  /* Se exporta ce e PE ECRAN, deci abia dupa Apply. */
  const exportable = applied !== null
    && (applied.report !== REPORT_TYPES.COSTS || applied.month > 0)
    && (applied.report !== REPORT_TYPES.CRITICAL || applied.priority > 0)

  const onExport = async () => {
    if (applied === null) return
    setExporting(true)
    try {
      if (applied.report === REPORT_TYPES.COSTS) {
        /* Aceeasi luna in From si in To: exportul primeste un interval. */
        await downloadCostsXlsx(applied.month, applied.month)
        return
      }
      await downloadReportXlsx(applied.report, exportParams(applied.report, {
        generateTime: applied.generateTime,
        priority: applied.priority,
        ticketTypes: applied.ticketTypes,
        groups: applied.groups,
        resolved: applied.resolved,
      }))
    } catch (error) {
      toast((error as Error).message, 'Export', 'error')
    } finally {
      setExporting(false)
    }
  }

  /*
   * Cand are Apply-ul cu ce sa ceara raportul.
   *
   * In macheta, campurile astea erau `required` pe formular, deci trimiterea
   * se oprea singura. Aici butonul e stins: un buton care promite un raport si
   * raspunde cu o eroare e mai rau decat unul stins care spune de ce.
   */
  const runnable =
    report === REPORT_TYPES.CRITICAL
      ? priority > 0
      : report === REPORT_TYPES.OPEN
        ? ticketTypes.length > 0
        : report === REPORT_TYPES.COSTS
          ? month > 0
          : report > 0

  const onApply = () => {
    setApplied({ report, generateTime, priority, ticketTypes, groups, month, resolved: draft })
    /* La trimitere, panoul se strange - `$('#collapseFilter').removeClass('in')`. */
    setFiltersOpen(false)
  }

  return (
    <CompanyReportsFrame current="easeedesk">
      <div id="content">
        <h3>
          <strong>Easeedesk</strong> reports
        </h3>
        <hr />

        {/*
          Filtrul in stanga, exportul in dreapta - randul de sub titlu din
          `easeedesk/index.php`. Iconitele sunt Font Awesome, nu Glyphicon:
          fontul ala nu se serveste sub `/app` si ar iesi casute goale.
        */}
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="col-md-6">
            <button
              type="button"
              className="btn btn-sm btn-default"
              id="filterToggle"
              aria-expanded={filtersOpen}
              aria-controls="collapseFilter"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              <i className="fa fa-filter" aria-hidden="true"></i> Filter
            </button>
          </div>

          <div className="col-md-6 align-right">
            {report === REPORT_TYPES.COSTS && shell.data.canBuildCosts ? (
              <button type="button" className="btn btn-sm btn-default" id="costBuild" onClick={() => setBuilding(true)}>
                <i className="fa fa-calculator" aria-hidden="true"></i> Work out a month
              </button>
            ) : null}{' '}
            {exportable ? (
              <button type="button" className="btn btn-sm btn-success" id="exportTable" disabled={exporting} onClick={() => void onExport()}>
                <i className="fas fa-download" aria-hidden="true"></i> Export
              </button>
            ) : null}
          </div>
        </div>

        {!shell.data.ready ? (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> The ticket tables are not installed on
            this database, so there are no numbers to report.
          </div>
        ) : null}

        {shell.data.allCompanies ? (
          <p className="text-muted">Counting across every company you can see.</p>
        ) : null}

        {/*
          PANOUL DE FILTRE, ca in macheta: o cutie gri, deschisa la intrare, cu
          campurile pe coloana din mijloc si Apply sub ele.

          Listele sunt bootstrap-select (`SelectPicker`), ca acolo: report_type,
          priority, cost_month, ticket_types[], groups[], users[], toate cu
          `data-live-search` si `data-size`. `name` ramane al portalului, deci
          <select>-ul ascuns poarta acelasi id.

          FARA `form-control` pe invelis. In `od_console.css` campul de text,
          `body.od-console .form-control:not([type="checkbox"]):not([type="radio"])`
          (0,4,1), bate regula care dezbraca invelisul (0,3,1) si iese o cutie in
          alta - cum s-a vazut pe Customer Services. Felul de raport are
          `fit-width` (`data-width="fit"`): la 220px "Incidents Assigned per
          Support Group" se taia.
        */}
        <div className={filtersOpen ? 'collapse in' : 'collapse'} id="collapseFilter">
          <div className="well" style={{ backgroundColor: '#f5f5f5' }}>
            <div className="row">
              <div className="col-md-4"></div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="report_type" className="control-label">
                    Report type:
                  </label>
                  <SelectPicker
                    id="report_type"
                    name="report_type"
                    className="fit-width"
                    title="Select.."
                    size={5}
                    value={report > 0 ? String(report) : ''}
                    onChange={(next) => setReport(Number(next))}
                    options={shell.data.reports.map((r) => ({ value: String(r.id), label: r.label }))}
                  />
                </div>

                {report === 0 || report === REPORT_TYPES.OPEN || report === REPORT_TYPES.RESOLVED || report === REPORT_TYPES.COSTS ? null : (
                  <div className="form-group">
                    {/* In portal erau doua butoane radio iCheck, nu o lista. */}
                    <label htmlFor="generate_time" className="control-label">
                      Report generate by:
                    </label>
                    <select
                      id="generate_time"
                      aria-label="Generate time"
                      className="form-control"
                      value={generateTime}
                      onChange={(e) => setGenerateTime(e.target.value as GenerateTime)}
                    >
                      {shell.data.generateTimes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {report === REPORT_TYPES.CRITICAL ? (
                  <div className="form-group">
                    <label htmlFor="priority" className="control-label">
                      Critical priority:
                    </label>
                    {/* 0 e "nimic ales": butonul scrie titlul, ca optiunea `data-hidden` din portal. */}
                    <SelectPicker
                      id="priority"
                      name="priority"
                      title="Pick a priority"
                      size={5}
                      value={priority > 0 ? String(priority) : ''}
                      onChange={(next) => setPriority(Number(next))}
                      options={shell.data.priorities.map((one) => ({ value: String(one.ID), label: one.priority_name }))}
                    />
                  </div>
                ) : null}

                {report === REPORT_TYPES.COSTS ? (
                  shell.data.costPeriods.length === 0 ? (
                    <div className="alert alert-info" role="alert" style={{ marginBottom: 0 }}>
                      <i className="fas fa-info-circle" aria-hidden="true"></i> No month has been costed yet. Give a
                      service a cost in the service catalogue, then work out a month there.
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="cost_month" className="control-label">
                        Month:
                      </label>
                      <SelectPicker
                        id="cost_month"
                        name="cost_month"
                        title="Select.."
                        size={8}
                        value={String(month)}
                        onChange={(next) => setCostMonth(Number(next))}
                        options={shell.data.costPeriods.map((one) => ({ value: String(one.ym), label: one.label }))}
                      />
                    </div>
                  )
                ) : report === 0 ? null : (
                  <div className="form-group">
                    <label htmlFor="ticket_types" className="control-label">
                      Ticket types:
                    </label>
                    <SelectPicker
                      id="ticket_types"
                      name="ticket_types[]"
                      title="Select.."
                      size={5}
                      multiple
                      value={ticketTypes.map(String)}
                      onChange={(next) => setTicketTypes(next.map(Number))}
                      options={shell.data.ticketTypes.map((t) => ({ value: String(t.ID), label: t.type_name }))}
                    />
                  </div>
                )}

                {report === REPORT_TYPES.OPEN || report === REPORT_TYPES.RESOLVED ? (
                  <div className="form-group">
                    <label htmlFor="groups" className="control-label">
                      Support groups:
                    </label>
                    <SelectPicker
                      id="groups"
                      name="groups[]"
                      title="Select.."
                      size={5}
                      multiple
                      value={groups.map(String)}
                      onChange={(next) => {
                        setGroups(next.map(Number))
                        /* Alt grup, alta lista de oameni: ce era bifat nu mai e in ea. */
                        setUsers([])
                      }}
                      options={shell.data.groups.map((g) => ({ value: String(g.ID), label: g.name }))}
                    />
                  </div>
                ) : null}

                {report === REPORT_TYPES.RESOLVED ? (
                  <>
                    <div className="form-group">
                      <label htmlFor="admin" className="control-label">
                        User:
                      </label>
                      <SelectPicker
                        id="admin"
                        name="users[]"
                        className="select-user-values"
                        title="Select.."
                        size={5}
                        multiple
                        value={users.map(String)}
                        onChange={(next) => setUsers(next.map(Number))}
                        options={(groupUsers.data?.users ?? []).map((u) => ({ value: String(u.ID), label: u.user_name }))}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="date_from" className="control-label">
                        Date from
                      </label>
                      <input id="date_from" type="text" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="date_to" className="control-label">
                        Date to
                      </label>
                      <input id="date_to" type="text" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="oncall" title="Check OnCall for tickets resolved out of office hours (weekends, before 08:00 or after 19:00)">
                        OnCall&nbsp;
                        <input id="oncall" type="checkbox" checked={oncall} onChange={(e) => setOncall(e.target.checked)} />
                      </label>
                    </div>
                    <div className="form-group">
                      <label htmlFor="nagios" title="Only the tickets opened by the monitoring system, with their operation timeline and work classification">
                        Nagios&nbsp;
                        <input id="nagios" type="checkbox" checked={nagios} onChange={(e) => setNagios(e.target.checked)} />
                      </label>
                    </div>
                  </>
                ) : null}
              </div>
              <div className="col-md-4"></div>
            </div>

            {/*
              APPLY. In macheta, TOATE cele sase rapoarte se cereau la
              trimiterea formularului, nu la fiecare atingere de filtru - deci
              pana aici nu pleaca nicio cerere.
            */}
            <div className="row">
              <div className="col-md-4"></div>
              <div className="col-md-4">
                <button type="button" className="btn btn-sm btn-primary" id="applyFilters" disabled={!runnable} onClick={onApply}>
                  Apply
                </button>

                {report === REPORT_TYPES.CRITICAL && priority === 0 ? (
                  shell.data.priorities.length === 0 ? (
                    <div className="alert alert-info" style={{ marginTop: 10 }}>
                      No priority is set up for tickets, so there is nothing to count here.
                    </div>
                  ) : (
                    <p className="text-muted small" style={{ marginTop: 6 }}>
                      Pick a priority to see the report.
                    </p>
                  )
                ) : null}

                {report === REPORT_TYPES.OPEN && ticketTypes.length === 0 ? (
                  shell.data.ticketTypes.length === 0 ? (
                    <div className="alert alert-info" style={{ marginTop: 10 }}>
                      No ticket type is set up, so there is nothing to count here.
                    </div>
                  ) : (
                    <p className="text-muted small" style={{ marginTop: 6 }}>
                      Pick at least one ticket type to see the report.
                    </p>
                  )
                ) : null}

                {report === REPORT_TYPES.RESOLVED && shown !== REPORT_TYPES.RESOLVED ? (
                  <p className="text-muted small" style={{ marginTop: 6 }}>
                    Pick the period the tickets were resolved in, then press Apply.
                  </p>
                ) : null}
              </div>
              <div className="col-md-4"></div>
            </div>
          </div>
        </div>

        {busy ? <Spinner /> : null}

        {reportError ? (
          isForbidden(reportError) ? (
            <Forbidden />
          ) : (
            <LoadFailed what="This report" error={reportError} onRetry={() => void active?.refetch()} />
          )
        ) : null}

        {shown === REPORT_TYPES.CRITICAL && critical.data ? <CriticalPanel data={critical.data} /> : null}
        {shown === REPORT_TYPES.STATUS && status.data ? <StatusPanel data={status.data} /> : null}
        {shown === REPORT_TYPES.PRIORITY && trends.data ? <PriorityPanel data={trends.data} /> : null}
        {shown === REPORT_TYPES.OPEN && open.data ? <OpenPanel data={open.data} /> : null}
        {shown === REPORT_TYPES.RESOLVED && resolved.data ? <ResolvedPanel data={resolved.data} /> : null}
        {shown === REPORT_TYPES.COSTS && (applied?.month ?? 0) > 0 && costs.data ? <CostsPanel data={costs.data} /> : null}

        {building ? (
          <CostSnapshotModal
            month={month}
            onClose={() => setBuilding(false)}
            onDone={(period) => {
              setBuilding(false)
              if (period > 0) {
                setCostMonth(period)
                /* Luna abia calculata e si cea de pe ecran: raportul aplicat o urmeaza. */
                setApplied((was) => (was && was.report === REPORT_TYPES.COSTS ? { ...was, month: period } : was))
              }
              /* Fila intreaga (lista Month vine cu ea) si costurile: prefixul cheii le prinde pe amandoua. */
              void queryClient.invalidateQueries({ queryKey: easeedeskReportsShellQuery.queryKey })
            }}
          />
        ) : null}
      </div>
    </CompanyReportsFrame>
  )
}
